import { FC } from 'react';

import { ACCOUNTS_CONTROL_MAX_USERS } from 'utils/constants/accounts';

import { BottomSheet, Link, Modal, Text, VSpacing, VSpacingContainer } from '@hh.ru/magritte-ui';
import { HeadersTypography, TextTypography } from '@hh.ru/magritte-ui-typography/types';
import { CrossOutlinedSize24 } from '@hh.ru/magritte-ui/icon';

import useOnOffState from 'hooks/common/useOnOffState';
import useTrls from 'hooks/translation/useTrls';

import { MagritteModalHeader } from 'components/common/MagritteModalHeader/MagritteModalHeader';

import { User } from 'store/models/accounts';

interface UsersListProps {
    users: User[];
    textTypography: TextTypography | HeadersTypography;
}

interface UserItemProps {
    user: User;
    textTypography: TextTypography | HeadersTypography;
}

const TrlKeys = {
    showAll: 'accounts.control.users.showAll',
    title: 'accounts.control.users.title',
};

const UserItem: FC<UserItemProps> = ({ user: { name, email }, textTypography }) => (
    <p>
        <Text Element="span" typography={textTypography}>
            {name}
        </Text>
        <Text Element="span" typography={textTypography} style="secondary">{` (${email})`}</Text>
    </p>
);

export const UsersList: FC<UsersListProps> = ({ users, textTypography }) => {
    const [isVisible, showModal, hideModal] = useOnOffState(false);
    const trl = useTrls();

    const visibleUsers = users.slice(0, ACCOUNTS_CONTROL_MAX_USERS);
    const exceedsMaxVisibleUsers = users.length > ACCOUNTS_CONTROL_MAX_USERS;
    const title = trl(TrlKeys.title);

    const usersList = (
        <VSpacingContainer default={8}>
            {users.map((user) => (
                <UserItem key={user.id} user={user} textTypography={textTypography} />
            ))}
        </VSpacingContainer>
    );

    return (
        <>
            {visibleUsers.map((user) => (
                <UserItem key={user.id} user={user} textTypography={textTypography} />
            ))}
            {exceedsMaxVisibleUsers && (
                <>
                    <VSpacing default={8} />
                    <Link Element="button" onClick={showModal}>
                        <Text typography="subtitle-2-semibold">{trl(TrlKeys.showAll)}</Text>
                    </Link>
                    <Modal
                        visible={isVisible}
                        onClose={hideModal}
                        header={
                            <MagritteModalHeader
                                title={title}
                                actions={<CrossOutlinedSize24 initialColor="secondary" onClick={hideModal} />}
                            />
                        }
                    >
                        {usersList}
                    </Modal>
                    <BottomSheet visible={isVisible} onClose={hideModal}>
                        <Text typography={textTypography}>{title}</Text>
                        <VSpacing default={12} />
                        {usersList}
                    </BottomSheet>
                </>
            )}
            <VSpacing default={8} />
        </>
    );
};
